import { useTranslation } from 'react-i18next';

import { ChartHeaderActions } from '@/components/charts/actions';
import { MetricChart } from '@/components/charts/generic';
import ContentBlock from '@/components/molecules/ContentBlock';
import type { DateRange } from '@/components/molecules/DateRangePicker/DateRangePicker';
import type { RetryProps } from '@/components/retryFailedQueries';
import type { ThresholdRange, TiltDataPoint } from '@/domain/behavior';
import type { ApiError } from '@/domain/shared/errors';

import { useOverviewTiltChartData } from '../../hooks/useOverviewTiltChartData';

interface TiltOverviewChartProps {
    dateRange: DateRange;
    onDateChange: (data: DateRange) => void;
    tiltDataPoints: TiltDataPoint[];
    tiltThreshold: ThresholdRange;
    isLoading: boolean;
    error: ApiError | null;
    /** Try again in the chart's error state. */
    retry?: RetryProps;
}

export default function TiltOverviewChart(props: TiltOverviewChartProps) {
    const {
        dateRange,
        isLoading = false,
        error,
        retry,
        onDateChange,
        tiltDataPoints,
        tiltThreshold,
    } = props;
    const { t } = useTranslation('behaviorModule');

    const chart = useOverviewTiltChartData({ tiltDataPoints, tiltThreshold });

    return (
        <ContentBlock
            title={t('tiltView.title')}
            containerRef={chart.fullScreenContainerRef}
            headerActions={
                <ChartHeaderActions
                    chartRef={chart.chartRef}
                    chartName="overview-tilt-chart"
                    csvExport={false}
                    isFullScreen={chart.isFullScreen}
                    onFullScreenToggle={chart.handleFullScreenToggle}
                    dateFrom={dateRange.dateFrom}
                    dateTo={dateRange.dateTo}
                    onDateChange={onDateChange}
                    defaultPresetId="last7days"
                    disabled={isLoading}
                />
            }>
            <MetricChart
                legendRowLayout="centered"
                kind="line"
                data={chart.data}
                options={chart.options}
                legendItems={chart.legendItems}
                visibleSeries={chart.visibleSeries}
                onToggleSeries={chart.toggleSeries}
                chartRef={chart.chartRef}
                height={chart.isFullScreen ? 400 : 230}
                isLoading={isLoading}
                error={error}
                onRetry={retry?.onRetry}
                isRetrying={retry?.isRetrying}
            />
        </ContentBlock>
    );
}
